// Shared metadata about scrap-metal market clusters used across public
// /rates pages and the admin dashboard. The DB is the source of truth for
// prices; this file just adds the human descriptions and SEO context that
// don't belong in a price row.

export interface ClusterMeta {
  slug: string;
  name: string;
  state: string;
  region: "East" | "West" | "North" | "South" | "Central";
  blurb: string;
  industries: string[];
}

export const CLUSTERS: ClusterMeta[] = [
  {
    slug: "jamshedpur",
    name: "Jamshedpur",
    state: "Jharkhand",
    region: "East",
    blurb:
      "Home to Tata Steel and India's oldest integrated steel ecosystem, Jamshedpur sets the benchmark for HMS and sponge iron rates across the Eastern industrial belt.",
    industries: ["Integrated steel mills", "Auto-grade flat products", "Heavy engineering"],
  },
  {
    slug: "durgapur",
    name: "Durgapur",
    state: "West Bengal",
    region: "East",
    blurb:
      "Durgapur's induction-furnace cluster consumes the highest volume of HMS 1 and HMS 2 in eastern India, with prices closely tracking SAIL's billet realisations.",
    industries: ["Induction furnaces", "Long products", "Sponge iron"],
  },
  {
    slug: "raipur",
    name: "Raipur",
    state: "Chhattisgarh",
    region: "Central",
    blurb:
      "The Raipur–Bhilai belt is India's largest sponge iron hub. Scrap and sponge iron rates here drive secondary steel pricing across central India.",
    industries: ["Sponge iron", "Rolling mills", "Ferro alloys"],
  },
  {
    slug: "mandi-gobindgarh",
    name: "Mandi Gobindgarh",
    state: "Punjab",
    region: "North",
    blurb:
      "Known as India's 'Steel City of the North', Mandi Gobindgarh's re-rolling mills and induction furnaces post the country's most-watched ex-yard HMS rates.",
    industries: ["Re-rolling mills", "TMT bars", "Wire rod"],
  },
  {
    slug: "mumbai",
    name: "Mumbai",
    state: "Maharashtra",
    region: "West",
    blurb:
      "Mumbai-Taloja prices reflect imported scrap landed at JNPT and Mundra, plus domestic supply moving to Western India's auto and infrastructure customers.",
    industries: ["Import-fed yards", "Auto components", "Construction"],
  },
  {
    slug: "ahmedabad",
    name: "Ahmedabad",
    state: "Gujarat",
    region: "West",
    blurb:
      "Ahmedabad's scrap rates are anchored to ship-breaking output from Alang-Sosiya and feed Gujarat's induction and rolling clusters.",
    industries: ["Ship-breaking feed", "Induction furnaces", "Re-rollers"],
  },
  {
    slug: "chennai",
    name: "Chennai",
    state: "Tamil Nadu",
    region: "South",
    blurb:
      "South India's largest scrap consumption cluster — Chennai prices factor in port-side imported HMS and supply to Sriperumbudur's auto OEM ecosystem.",
    industries: ["Auto OEM supply", "Induction furnaces", "Stainless steel"],
  },
  {
    slug: "ghaziabad",
    name: "Ghaziabad",
    state: "Uttar Pradesh",
    region: "North",
    blurb:
      "The Delhi-NCR scrap belt covers Ghaziabad, Muzaffarnagar and Loni — feeding North India's construction and rebar demand with consistently liquid HMS pricing.",
    industries: ["Re-rolling mills", "Rebar", "Engineering scrap"],
  },
];

export const getClusterBySlug = (slug: string): ClusterMeta | undefined =>
  CLUSTERS.find((c) => c.slug === slug.toLowerCase());

export const getRelatedClusters = (slug: string, count = 3): ClusterMeta[] => {
  const current = getClusterBySlug(slug);
  if (!current) return CLUSTERS.slice(0, count);
  const sameRegion = CLUSTERS.filter((c) => c.slug !== slug && c.region === current.region);
  const others = CLUSTERS.filter((c) => c.slug !== slug && c.region !== current.region);
  return [...sameRegion, ...others].slice(0, count);
};

export const formatINR = (n: number) =>
  "₹" + Math.round(n).toLocaleString("en-IN");

// ── Material grade families ────────────────────────────────────────────────
// Rates are always stored per metric tonne. Plastic scrap is traded per kg in
// India, so rows flagged as "KG" are divided by 1000 for display.

export const GRADE_FAMILY_ORDER = [
  "Ferrous Scrap",
  "Finished Steel",
  "Minerals & DRI",
  "Plastic Scrap",
] as const;

export type GradeFamilyName = (typeof GRADE_FAMILY_ORDER)[number];

export const GRADE_FAMILY_BLURB: Record<string, string> = {
  "Ferrous Scrap": "Melting scrap grades feeding induction furnaces and EAF mills.",
  "Finished Steel": "Semi-finished and finished long products from secondary mills.",
  "Minerals & DRI": "Furnace feed and flux — sponge iron, pellets and dolomite.",
  "Plastic Scrap": "Post-consumer and industrial polymer scrap, quoted per kilogram.",
};

export const unitLabel = (unit?: string | null) => (unit === "KG" ? "₹/kg" : "₹/MT");

/** Price rows are stored per MT; render per-kg grades at their trade unit. */
export const formatRate = (pricePerMt: number, unit?: string | null) => {
  if (unit === "KG") {
    const perKg = pricePerMt / 1000;
    return "₹" + perKg.toLocaleString("en-IN", { maximumFractionDigits: perKg < 100 ? 2 : 0 });
  }
  return formatINR(pricePerMt);
};

export const sortGradeFamilies = <T extends { grade_family?: string | null }>(rows: T[]) => {
  const groups = new Map<string, T[]>();
  for (const r of rows) {
    const fam = r.grade_family || "Ferrous Scrap";
    const arr = groups.get(fam) ?? [];
    arr.push(r);
    groups.set(fam, arr);
  }
  return Array.from(groups.entries()).sort(
    (a, b) =>
      (GRADE_FAMILY_ORDER.indexOf(a[0] as GradeFamilyName) + 1 || 99) -
      (GRADE_FAMILY_ORDER.indexOf(b[0] as GradeFamilyName) + 1 || 99),
  );
};
